import { PopIcon } from './icons/PopIcons';
// ==========================================
// RoomGuard — Prevents accidental navigation away from a room/game
// Shows confirmation dialog before leaving
// ==========================================

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../stores/gameStore';
import { useSocket } from '../hooks/useSocket';

export default function RoomGuard() {
  const { room, isPlaying, gameId } = useGameStore();
  const { leaveRoom } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  const isInRoom = !!room;
  const isOnRoomPage = location.pathname.startsWith('/lobby') || location.pathname.startsWith('/game');

  // Intercept browser back/close
  useEffect(() => {
    if (!isInRoom) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Vous êtes dans une partie. Voulez-vous vraiment quitter ?';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isInRoom]);

  // Intercept in-app link clicks
  useEffect(() => {
    if (!isInRoom) return;

    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
      if (!target) return;

      const href = target.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('#')) return;

      // Allow navigation within room/game pages
      if (href.startsWith('/lobby') || href.startsWith('/game')) return;

      // Intercept navigation
      e.preventDefault();
      e.stopPropagation();
      setPendingPath(href);
      setShowConfirm(true);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isInRoom]);

  const handleConfirmLeave = useCallback(async () => {
    setShowConfirm(false);
    await leaveRoom();
    if (pendingPath) {
      navigate(pendingPath);
      setPendingPath(null);
    }
  }, [leaveRoom, navigate, pendingPath]);

  const handleCancel = useCallback(() => {
    setShowConfirm(false);
    setPendingPath(null);
  }, []);

  return (
    <AnimatePresence>
      {showConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={handleCancel}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="w-full max-w-sm p-6 rounded-2xl text-center"
            style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="text-4xl mb-3"><PopIcon name="🚪"/></div>
            <h2 className="font-arcade text-base text-white mb-2">
              Quitter la partie ?
            </h2>
            <p className="text-sm text-gray-400 mb-5">
              {isPlaying
                ? 'Tu es en pleine partie ! Si tu quittes, ta progression sera perdue.'
                : 'Tu es dans un salon. Si tu quittes, tu devras rejoindre à nouveau.'}
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className="flex-1 py-3 rounded-xl text-sm font-mono transition-all"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#ccc',
                }}
              >
                Rester
              </button>
              <button
                onClick={handleConfirmLeave}
                className="flex-1 py-3 rounded-xl text-sm font-mono transition-all"
                style={{
                  background: 'rgba(255,85,85,0.12)',
                  border: '1px solid rgba(255,85,85,0.25)',
                  color: '#ff6b6b',
                }}
              >
                Quitter
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
